import axios, { AxiosInstance, AxiosResponse } from 'axios';

export type LoyaltyRewardStatus = 'PENDING' | 'REDEEMED' | 'EXPIRED';

export interface LoyaltyReward {
  id: string;
  cnpj: string;
  customerId: string;
  campaignId: string;
  rewardType: 'COUPON' | 'ITEM' | 'CUSTOM';
  rewardValue?: string;
  couponCode?: string;
  couponValue?: number;
  couponMinOrderValue?: number;
  couponExpiresAt?: string;
  rewardDescription?: string;
  status: LoyaltyRewardStatus;
  issuedAt: string;
  redeemedAt?: string;
  expiresAt?: string;
  metadata?: Record<string, unknown>;
}

export interface ListLoyaltyRewardsParams {
  customerId?: string;
  status?: LoyaltyRewardStatus;
  campaignId?: string;
  cnpj?: string;
}

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

const withAuthInterceptor = (client: AxiosInstance): AxiosInstance => {
  client.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem('accessToken');
      if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error),
  );

  return client;
};

class LoyaltyRewardService {
  private client: AxiosInstance;

  constructor() {
    this.client = withAuthInterceptor(
      axios.create({
        baseURL: API_BASE_URL,
        timeout: 15000,
      }),
    );
  }

  private buildParams(params?: ListLoyaltyRewardsParams) {
    if (!params) {
      return undefined;
    }
    const result: Record<string, any> = {};
    if (params.customerId?.trim()) {
      result.customerId = params.customerId.trim();
    }
    if (params.status) {
      result.status = params.status;
    }
    if (params.campaignId?.trim()) {
      result.campaignId = params.campaignId.trim();
    }
    if (params.cnpj?.trim()) {
      const digits = params.cnpj.replace(/\D/g, '');
      if (digits) {
        result.cnpj = digits;
      }
    }
    return result;
  }

  async list(params?: ListLoyaltyRewardsParams): Promise<LoyaltyReward[]> {
    try {
      const response: AxiosResponse<LoyaltyReward[]> = await this.client.get('/admin/loyalty/rewards', {
        params: this.buildParams(params),
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Erro ao listar recompensas de fidelidade');
    }
  }

  async markRedeemed(id: string, options?: { cnpj?: string }): Promise<LoyaltyReward> {
    try {
      const response: AxiosResponse<LoyaltyReward> = await this.client.post(
        `/admin/loyalty/rewards/${id}/redeem`,
        {},
        {
          params:
            options?.cnpj && options.cnpj.trim()
              ? { cnpj: options.cnpj.replace(/\D/g, '') }
              : undefined,
        },
      );
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Erro ao marcar recompensa como resgatada');
    }
  }
}

export const loyaltyRewardService = new LoyaltyRewardService();
export default loyaltyRewardService;
